import React from 'react';
import './WeekProgress.css';
import { getCurrentDayIndex } from '../../features/schedule/scheduleData';
import type { DaySchedule } from '../../types';

interface WeekProgressProps {
  completedTasks: number;
  totalTasks: number;
  tasks: Record<string, boolean>;
  schedule: DaySchedule[];
  activeDays: boolean[];
}

export const WeekProgress: React.FC<WeekProgressProps> = ({
  completedTasks,
  totalTasks,
  tasks,
  schedule,
  activeDays,
}) => {
  const currentDayIndex = getCurrentDayIndex();
  const percent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const isSunday = currentDayIndex === -1;

  const todaySchedule = !isSunday && currentDayIndex < schedule.length ? schedule[currentDayIndex] : null;
  const todayActive = !isSunday && currentDayIndex >= 0 && activeDays[currentDayIndex];
  const todayCompleted = todaySchedule && todayActive
    ? todaySchedule.tasks.filter((t) => tasks[t.id]).length
    : 0;
  const todayTotal = todaySchedule && todayActive ? todaySchedule.tasks.length : 0;

  return (
    <div className="week-progress" style={{ animationDelay: '0.3s' }}>
      {isSunday ? (
        <div className="week-progress-today-banner week-progress-sunday">
          <span className="week-progress-today-emoji">☀️</span>
          <div className="week-progress-today-text">
            <strong>Sunday — Rest Day!</strong>
            <span>Recharge for the week ahead</span>
          </div>
        </div>
      ) : todaySchedule && todayActive ? (
        <div
          className="week-progress-today-banner"
          style={{ '--banner-color': todaySchedule.color } as React.CSSProperties}
        >
          <span className="week-progress-today-emoji">
            {todayCompleted === todayTotal ? '🎉' : '🧹'}
          </span>
          <div className="week-progress-today-text">
            <strong>Today: {todaySchedule.day} — {todaySchedule.theme}</strong>
            <span>
              {todayCompleted === todayTotal
                ? "All done! Great job!"
                : `${todayCompleted}/${todayTotal} tasks completed`}
            </span>
          </div>
        </div>
      ) : !isSunday && todaySchedule ? (
        <div className="week-progress-today-banner week-progress-sunday">
          <span className="week-progress-today-emoji">😌</span>
          <div className="week-progress-today-text">
            <strong>{todaySchedule.day} — Rest Day</strong>
            <span>This day is set to inactive</span>
          </div>
        </div>
      ) : null}

      <div className="week-progress-bar-section">
        <div className="week-progress-label">
          <span>Weekly Progress</span>
          <span className="week-progress-percent">{percent}%</span>
        </div>
        <div className="week-progress-track">
          <div className="week-progress-fill" style={{ width: `${percent}%` }} />
        </div>
        <div className="week-progress-count">
          {completedTasks} of {totalTasks} tasks complete
        </div>
      </div>

      <div className="week-progress-dots">
        {schedule.map((day, i) => {
          const isActive = activeDays[i];
          const dayCompleted = day.tasks.filter((t) => tasks[t.id]).length;
          const dayTotal = day.tasks.length;
          const dayDone = isActive && dayCompleted === dayTotal;
          const dayStarted = isActive && dayCompleted > 0;
          return (
            <div
              key={day.day}
              className={`week-dot ${i === currentDayIndex ? 'week-dot--today' : ''} ${dayDone ? 'week-dot--done' : dayStarted ? 'week-dot--partial' : ''} ${!isActive ? 'week-dot--inactive' : ''}`}
              style={{ '--dot-color': day.color } as React.CSSProperties}
            >
              <div className="week-dot-circle">
                {dayDone && (
                  <svg viewBox="0 0 16 16" width="10" height="10">
                    <polyline points="3 8 6.5 11 13 5" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className="week-dot-label">{day.day.slice(0, 3)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
